// The console side of the operations-password gate (server/opsgate.go).
//
// The vessel/operation limits, the operations catalog and the vessel-operation
// links are the client's operating SPEC — the numbers behind every workability
// verdict — and when the client has an operations password set, the server
// refuses to mutate them without it. This module owns the whole flow: try the
// mutation, and when the server's distinct 403 comes back, put up a confirm
// dialog that states EXACTLY what is about to change (concrete before → after
// values, in the operator's units), demands the word CHANGE typed back, and
// takes the operations password. Cancel sends nothing.
//
// The password is cached in memory only — never localStorage, never a cookie —
// so a batch of edits asks for it once, but every edit still gets its own
// confirm dialog: the point of the gate is that no spec number ever changes
// without someone reading a sentence that says which number, from what, to what.

import {
  isOpsGateRefusal,
  isServerBusy,
  OPS_PASSWORD_INVALID,
  OPS_PASSWORD_THROTTLED,
} from "../data/api.js";
import type { ApiVessel, Result, VesselInput } from "../data/api.js";
import { RETRY_NOW, SERVER_BUSY, startCountdown, throttledOpsPassword } from "./retry.js";
import { fmtLimitLen } from "./units.js";

// --- change descriptions (pure, tested) --------------------------------------

/** What the confirm dialog states. `summary` is the one-sentence consequence
 *  ("…for ALL users of this console"); `lines` are the concrete values. */
export interface OpsChange {
  summary: string;
  lines: string[];
}

/** The word the operator must type back. One unambiguous token, uppercase. */
export const CONFIRM_WORD = "CHANGE";

interface LimitTriple {
  wave_m: number;
  wind_kn: number;
  task_hours: number;
}

function arrow(before: string, after: string): string {
  return before === after ? `${before} (unchanged)` : `${before} → ${after}`;
}

/** Before→after lines for the three limit numbers. `before` is null when there
 *  is nothing to compare against (a bespoke operation with no default). */
function limitLines(before: LimitTriple | null, beforeTag: string, after: LimitTriple): string[] {
  const wave = (t: LimitTriple): string => fmtLimitLen(t.wave_m);
  const wind = (t: LimitTriple): string => `${t.wind_kn} kn`;
  const hours = (t: LimitTriple): string => `${t.task_hours} h`;
  if (before === null) {
    return [
      `Wave ≤ ${wave(after)}`,
      `Wind ≤ ${wind(after)}`,
      `Task window ≥ ${hours(after)}`,
    ];
  }
  const tag = beforeTag === "" ? "" : ` ${beforeTag}`;
  return [
    `Wave ≤ ${arrow(wave(before) + tag, wave(after))}`,
    `Wind ≤ ${arrow(wind(before) + tag, wind(after))}`,
    `Task window ≥ ${arrow(hours(before) + tag, hours(after))}`,
  ];
}

/** Saving a limit row: replaces the client's own numbers, or the provisional
 *  default the row was showing, or sets first-ever numbers for a bespoke op. */
export function describeLimitSave(
  vesselName: string,
  opLabel: string,
  before: LimitTriple | null,
  beforeIsDefault: boolean,
  after: LimitTriple,
): OpsChange {
  return {
    summary:
      `This changes the ${opLabel} limits for ${vesselName} ` +
      `for ALL users of this console.`,
    lines: limitLines(before, beforeIsDefault ? "(default)" : "", after),
  };
}

/** Resetting a limit row: the custom numbers go, the default (if any) returns. */
export function describeLimitReset(
  vesselName: string,
  opLabel: string,
  current: LimitTriple,
  fallback: LimitTriple | null,
): OpsChange {
  const lines =
    fallback !== null
      ? limitLines(current, "", fallback).map((l) => l.replace("(unchanged)", "(same as default)"))
      : [
          `Wave ≤ ${fmtLimitLen(current.wave_m)} → none`,
          `Wind ≤ ${current.wind_kn} kn → none`,
          `Task window ≥ ${current.task_hours} h → none`,
        ];
  return {
    summary:
      `This removes the custom ${opLabel} limits for ${vesselName} ` +
      `for ALL users of this console` +
      (fallback !== null
        ? ` — the row falls back to the provisional preset.`
        : ` — a bespoke operation has no preset to fall back to.`),
    lines,
  };
}

/** Adding an operation to the catalog (it appears on every vessel's table). */
export function describeOperationAdd(label: string, kind: string | null): OpsChange {
  return {
    summary:
      `This adds the operation ${label} to the catalog, on every vessel's ` +
      `limits table, for ALL users of this console.`,
    lines: [kind !== null ? `Preset family: ${kind}` : "Bespoke — no preset limits"],
  };
}

/** Deleting a catalog operation removes every vessel's limits for it too. */
export function describeOperationDelete(label: string): OpsChange {
  return {
    summary:
      `This deletes the operation ${label} from the catalog for ALL users of ` +
      `this console — every vessel's saved limits for it are removed with it.`,
    lines: [],
  };
}

/** Ticking/unticking an operation on a vessel's table. */
export function describeVesselOpToggle(
  vesselName: string,
  opLabel: string,
  enabling: boolean,
): OpsChange {
  return {
    summary: enabling
      ? `This marks ${vesselName} as performing ${opLabel} ` +
        `for ALL users of this console.`
      : `This marks ${vesselName} as NOT performing ${opLabel} ` +
        `for ALL users of this console.`,
    lines: [],
  };
}

// --- vessel particulars and the vessel itself ---------------------------------
//
// The particulars are spec too. loa_m, beam_m, draft_m and roll_period_s feed
// the seakeeping maths; dp, day_only and nearshore gate whole operations. So a
// roll period edited from 12 s to 9 s moves a go/no-go call exactly as a wave
// limit does, and it gets the same stated-diff dialog.

/** How each particular is written out. Lengths follow the Units preference;
 *  seconds and knots are stored and shown as themselves. */
const PARTICULARS: ReadonlyArray<{
  key: keyof VesselInput;
  label: string;
  fmt: (v: number) => string;
}> = [
  { key: "loa_m", label: "Length (LOA)", fmt: fmtLimitLen },
  { key: "beam_m", label: "Beam", fmt: fmtLimitLen },
  { key: "draft_m", label: "Draft", fmt: fmtLimitLen },
  { key: "roll_period_s", label: "Roll period", fmt: (v) => `${v} s` },
  { key: "cruise_kn", label: "Cruise speed", fmt: (v) => `${v} kn` },
  { key: "max_kn", label: "Max speed", fmt: (v) => `${v} kn` },
];

const FLAGS: ReadonlyArray<{ key: keyof VesselInput; label: string }> = [
  { key: "dp", label: "Dynamic positioning" },
  { key: "day_only", label: "Daylight-only" },
  { key: "nearshore", label: "Nearshore" },
];

/** The registry spells "not set" as 0 for a numeric and "" for gear; the write
 *  shape spells it null. Compare and print in one vocabulary, or every edit
 *  reads as a change from zero. */
function storedParticulars(v: ApiVessel): VesselInput {
  const num = (x: number): number | null => (x > 0 ? x : null);
  return {
    name: v.name,
    loa_m: num(v.loa_m),
    beam_m: num(v.beam_m),
    roll_period_s: num(v.roll_period_s),
    draft_m: num(v.draft_m),
    cruise_kn: num(v.cruise_kn),
    max_kn: num(v.max_kn),
    dp: v.dp,
    day_only: v.day_only,
    nearshore: v.nearshore,
    gear: v.gear === "" ? null : v.gear,
  };
}

function numAt(v: VesselInput, key: keyof VesselInput): number | null {
  const raw = v[key];
  return typeof raw === "number" ? raw : null;
}

/** One particular, written out, or "not set" when the field is blank. */
function statedValue(
  v: VesselInput,
  key: keyof VesselInput,
  fmt: (n: number) => string,
): string {
  const n = numAt(v, key);
  return n === null ? "not set" : fmt(n);
}

/** Registering a vessel: it joins the fleet with the particulars given, and the
 *  verdicts it will be graded by follow from them. */
export function describeVesselAdd(after: VesselInput): OpsChange {
  const lines: string[] = [];
  for (const p of PARTICULARS) {
    const n = numAt(after, p.key);
    if (n !== null) lines.push(`${p.label}: ${p.fmt(n)}`);
  }
  const flags = FLAGS.filter((f) => after[f.key] === true).map((f) => f.label);
  if (flags.length > 0) lines.push(`Flags: ${flags.join(", ")}`);
  if (after.gear !== null && after.gear !== "") lines.push(`Gear: ${after.gear}`);
  if (lines.length === 0) lines.push("No particulars given — name only.");
  lines.push("No operations ticked yet, so it has no limits until you set them.");
  return {
    summary:
      `This adds the vessel ${after.name} to this console's fleet ` +
      `for ALL users, with the particulars below.`,
    lines,
  };
}

/** Editing particulars: only what actually changes is stated, because ten
 *  "(unchanged)" lines are how the one that matters gets skimmed past. */
export function describeVesselEdit(before: ApiVessel, after: VesselInput): OpsChange {
  const was = storedParticulars(before);
  const lines: string[] = [];
  if (was.name !== after.name) lines.push(`Name: ${was.name} → ${after.name}`);
  for (const p of PARTICULARS) {
    if (numAt(was, p.key) === numAt(after, p.key)) continue;
    lines.push(`${p.label}: ${statedValue(was, p.key, p.fmt)} → ${statedValue(after, p.key, p.fmt)}`);
  }
  for (const f of FLAGS) {
    if (was[f.key] === after[f.key]) continue;
    lines.push(`${f.label}: ${after[f.key] === true ? "no → yes" : "yes → no"}`);
  }
  if (was.gear !== after.gear) {
    lines.push(`Gear: ${was.gear ?? "none"} → ${after.gear ?? "none"}`);
  }
  if (lines.length === 0) lines.push("Nothing changes — every particular is as it was.");
  return {
    summary:
      `This changes the particulars of ${before.name} for ALL users of this ` +
      `console. They are inputs to its workability and seakeeping verdicts.`,
    lines,
  };
}

/** Deleting a vessel. This is the cascade: migration 0001 declares both
 *  vessel_operations and vessel_operation_limits ON DELETE CASCADE, so one
 *  request destroys every limit ever tuned for the hull — the exact rows the
 *  gate protects — and re-adding the vessel does not bring them back. So the
 *  dialog states the real counts, and names what goes, rather than saying
 *  "its limits are removed too" and leaving the operator to guess how many. */
export function describeVesselDelete(
  vesselName: string,
  limitLabels: string[],
  linkedLabels: string[],
): OpsChange {
  const tally = (n: number, one: string, many: string): string =>
    n === 1 ? `1 ${one}` : `${n} ${many}`;
  const named = (labels: string[]): string =>
    labels.length === 0 ? "" : ` — ${labels.join(", ")}`;
  return {
    summary:
      `This deletes the vessel ${vesselName} for ALL users of this console, ` +
      `and takes everything tuned for it. Deletion cascades and cannot be ` +
      `undone: re-adding the vessel does not bring the numbers back.`,
    lines: [
      `${tally(limitLabels.length, "saved limit", "saved limits")} destroyed` +
        named(limitLabels),
      `${tally(linkedLabels.length, "operation", "operations")} unlinked from ` +
        `${vesselName}${named(linkedLabels)}`,
    ],
  };
}

// --- the session password cache (memory only) --------------------------------

let cachedOpsPassword: string | null = null;

/** Forget the cached operations password (sign-out, tests). */
export function clearOpsPasswordCache(): void {
  cachedOpsPassword = null;
}

// --- the gated-mutation flow -------------------------------------------------

/** "cancelled" = the operator backed out of the dialog; nothing was sent. */
export type GatedResult<T> = Result<T> | "cancelled";

/**
 * Run one spec mutation through the gate. With no password cached, the
 * mutation is tried bare first — a client with no operations password set gets
 * exactly the old behavior, no dialog. The server's distinct 403 (or a cached
 * password, which proves the gate is active) brings up the confirm dialog, and
 * only the dialog's Apply sends again — with the password. The caller's edit
 * is never lost: a failure resolves like any API failure, a cancel resolves
 * "cancelled" with nothing sent.
 */
export async function gatedSpecMutation<T>(
  change: OpsChange,
  send: (opsPassword?: string) => Promise<Result<T>>,
): Promise<GatedResult<T>> {
  if (cachedOpsPassword === null) {
    const first = await send();
    if (first.ok || !isOpsGateRefusal(first)) return first;
    // The bare try's refusal is handed to the dialog, so a gate that is
    // currently throttled opens already holding rather than inviting a
    // password it has just declined to look at.
    return confirmDialog(change, send, first);
  }
  return confirmDialog(change, send);
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function confirmDialog<T>(
  change: OpsChange,
  send: (opsPassword?: string) => Promise<Result<T>>,
  opening?: Result<T>,
): Promise<GatedResult<T>> {
  return new Promise((resolve) => {
    const backdrop = el("div", "opsgate-backdrop");
    const card = el("form", "opsgate block");

    const bh = el("div", "bh");
    bh.appendChild(el("span", "bt", "Confirm operating-spec change"));
    card.appendChild(bh);

    card.appendChild(el("p", "opsgate-summary", change.summary));
    if (change.lines.length > 0) {
      const ul = el("ul", "opsgate-lines");
      for (const line of change.lines) ul.appendChild(el("li", undefined, line));
      card.appendChild(ul);
    }
    card.appendChild(
      el(
        "p",
        "note",
        "These numbers drive the console's workability calls, so changes are " +
          "protected by the operations password.",
      ),
    );

    const wordWrap = el("label", "fld");
    wordWrap.appendChild(el("span", "lab", `Type ${CONFIRM_WORD} to proceed`));
    const wordInput = el("input", "mono");
    wordInput.type = "text";
    wordInput.autocomplete = "off";
    wordWrap.appendChild(wordInput);
    card.appendChild(wordWrap);

    const pwWrap = el("label", "fld");
    pwWrap.appendChild(el("span", "lab", "Operations password"));
    const pwInput = el("input", "mono");
    pwInput.type = "password";
    pwInput.autocomplete = "off";
    if (cachedOpsPassword !== null) pwInput.value = cachedOpsPassword;
    pwWrap.appendChild(pwInput);
    card.appendChild(pwWrap);

    const err = el("p", "field-err");
    err.hidden = true;
    card.appendChild(err);

    const actions = el("div", "vform-actions");
    const cancelBtn = el("button", "btn", "Cancel");
    cancelBtn.type = "button";
    const applyBtn = el("button", "btn primary", "Apply change");
    applyBtn.type = "submit";
    actions.append(cancelBtn, applyBtn);
    card.appendChild(actions);

    backdrop.appendChild(card);
    document.body.appendChild(backdrop);

    // `held` is the server having named a wait that has not run out. It is kept
    // apart from the typed-word and password checks because it is the only one
    // the operator cannot clear by typing.
    let held = false;
    let stopCountdown: (() => void) | null = null;

    const refreshApply = (): void => {
      applyBtn.disabled =
        held || wordInput.value.trim() !== CONFIRM_WORD || pwInput.value === "";
    };

    /** `hold` marks the line as a wait rather than a refusal: amber, not the
     *  red that means the password was wrong. */
    const setErr = (message: string, hold: boolean): void => {
      err.textContent = message;
      err.classList.toggle("hold", hold);
      err.hidden = false;
    };

    /**
     * The gate refused for time rather than for the password. State the wait,
     * count it down, and hold Apply until it expires. The typed password and
     * the operator's edit both survive untouched — nothing was checked, so
     * there is nothing here to distrust and nothing to retype.
     */
    const holdFor = (seconds: number | null): void => {
      stopCountdown?.();
      stopCountdown = null;
      setErr(throttledOpsPassword(seconds), true);
      if (seconds === null) return; // no readable wait: say so, leave Apply live
      held = true;
      refreshApply();
      stopCountdown = startCountdown(
        seconds,
        (left) => setErr(throttledOpsPassword(left), true),
        () => {
          stopCountdown = null;
          held = false;
          setErr(RETRY_NOW, true);
          refreshApply();
        },
      );
    };

    refreshApply();
    wordInput.addEventListener("input", refreshApply);
    pwInput.addEventListener("input", () => {
      // A wait is still true however much the operator retypes, so it stays up.
      if (!held) err.hidden = true;
      refreshApply();
    });

    const close = (outcome: GatedResult<T>): void => {
      stopCountdown?.();
      document.removeEventListener("keydown", onKey);
      backdrop.remove();
      resolve(outcome);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") close("cancelled");
    };
    document.addEventListener("keydown", onKey);
    cancelBtn.addEventListener("click", () => close("cancelled"));
    backdrop.addEventListener("click", (event) => {
      if (event.target === backdrop) close("cancelled");
    });

    card.addEventListener("submit", (event: SubmitEvent) => {
      event.preventDefault();
      if (applyBtn.disabled) return;
      applyBtn.disabled = true;
      const password = pwInput.value;
      void send(password).then((res) => {
        if (res.ok) {
          cachedOpsPassword = password; // memory only, for this session's batch
          close(res);
          return;
        }
        if (res.code === OPS_PASSWORD_THROTTLED) {
          // Too many wrong ones lately, so the gate declined to check this one.
          // The cache is deliberately kept: an unexamined password has not been
          // shown to be wrong, and dropping it would make the operator retype a
          // password that may be perfectly good.
          holdFor(res.retryAfter ?? null);
          return;
        }
        if (isServerBusy(res)) {
          // Every argon2 slot was held: no hash ran, so this says nothing
          // about the password. Keep the dialog — the edit is still good and
          // the wait is a second.
          setErr(SERVER_BUSY, true);
          refreshApply();
          return;
        }
        if (isOpsGateRefusal(res)) {
          // Wrong (or rotated-away) password: keep the dialog and the edit,
          // drop the bad cache, let the operator retype.
          if (cachedOpsPassword === password) cachedOpsPassword = null;
          setErr(
            res.code === OPS_PASSWORD_INVALID
              ? "Wrong operations password."
              : "Operations password required.",
            false,
          );
          refreshApply();
          return;
        }
        // Any other failure surfaces at the call site like before the gate.
        close(res);
      });
    });

    // The bare try already met the throttle: open holding, with the wait
    // stated, rather than presenting a live form that cannot succeed yet.
    if (opening !== undefined && !opening.ok && opening.code === OPS_PASSWORD_THROTTLED) {
      holdFor(opening.retryAfter ?? null);
    }

    wordInput.focus();
  });
}
